// What the shell knows about collections: which list each belongs to, its
// icon and its item count.
import { useEffect, useState } from 'react';
import api from '../config/api';
import { useOnChanged } from './changes';
import { BookIcon, BoxIcon, DiceIcon, FilmIcon, GameIcon, HomeIcon } from '../ui/icons';

/** Owned collections (is_mine). */
export const ownedCollections = (inventories) => (inventories || []).filter((inv) => inv.is_mine);

/**
 * Collections shared with the caller: not theirs, and reached through a
 * share (an admin's "all accounts" list also holds others' collections,
 * which are not shared with them).
 */
export const sharedCollections = (inventories) => (inventories || [])
  .filter((inv) => !inv.is_mine && (inv.share_permission === 'read' || inv.share_permission === 'edit'));

const KIND_ICONS = { movie: FilmIcon, book: BookIcon, boardgame: DiceIcon, videogame: GameIcon, household: HomeIcon };

/** The icon of a collection: its default type's kind first, then any type's; a box otherwise. */
export function collectionIcon(inventory) {
  const types = inventory && inventory.item_types ? [...inventory.item_types] : [];
  types.sort((a, b) => Number(!!b.is_default) - Number(!!a.is_default));
  for (const type of types) {
    if (type.kind && KIND_ICONS[type.kind]) return KIND_ICONS[type.kind];
  }
  return BoxIcon;
}

/** "Jean-Dominique Quinet" → "JQ"; "jdquinet" → "JD". */
export function initials(name) {
  const words = String(name || '').trim().split(/[\s._-]+/).filter(Boolean);
  if (words.length === 0) return '?';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
}

/** A collection's item count, as GET /inventories gives it (undefined when absent). */
export const countOf = (inv) => (inv && inv.item_count != null ? Number(inv.item_count) : undefined);

/** Pending account requests (admins only): fetched once, then again when accounts change. */
export function usePendingAccounts(isAdmin) {
  const [count, setCount] = useState(0);
  const [version, setVersion] = useState(0);
  useOnChanged('accounts', () => setVersion((v) => v + 1));
  useEffect(() => {
    if (!isAdmin) return undefined;
    let alive = true;
    api.get('/admin/users')
      .then((r) => { if (alive) setCount((r.data || []).filter((u) => u.status === 'pending').length); })
      .catch(() => {});
    return () => { alive = false; };
  }, [isAdmin, version]);
  return isAdmin ? count : 0;
}
